/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { tokens as Tokens } from "@/utils/tokens";

import i18n from "@/i18n";
import { ITokens } from "@/interfaces/User.interface";
import { AUTH_TOKEN_NOT_VALID } from "@/constants";
import { isEmptyObject } from "./../../utils/object/isEmpty";
import { queryClient } from "@/utils/queryClient";

interface IRequestOptions extends RequestInit {
  headers?: Record<string, string>;
  withAuth?: boolean;
  queryParams?: Record<string, any>;
}
/**
 * Represents a utility class for making HTTP requests using the Fetch API.
 */
class FetchWrapper {
  tokensInstance = Tokens;
  private static instance: FetchWrapper | null = null;
  baseURL: string; // The base URL for the requests.
  options: IRequestOptions; // Additional request options that can be applied to all requests made by this instance.
  isRefreshingToken = false;

  private async refreshToken() {
    const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
    const { refresh: refreshToken } = userAuth || {};
    if (!refreshToken || this.tokensInstance.isTokenExpired(refreshToken)) {
      throw {
        code: AUTH_TOKEN_NOT_VALID,
        description: "",
        meta: "",
      };
    }
    if (this.isRefreshingToken) {
      return;
    }
    this.isRefreshingToken = true;
    const res = await this.post(`/tokens/refresh`, {
      refresh: refreshToken,
    });
    this.isRefreshingToken = false;
    // store tokens received to localstorage
    queryClient.setQueryData(["user-auth"], res);
  }

  /**
   * Creates an instance of FetchWrapper.
   *
   * @param {string} baseURL - The base URL for the requests.
   * @param {IRequestOptions} [options={}] - Additional request options to apply to all requests.
   */
  private constructor(baseURL: string, options: IRequestOptions = {}) {
    this.baseURL = baseURL;
    this.options = options;
  }

  static getInstance(baseURL: string, options: IRequestOptions = {}): FetchWrapper {
    if (!FetchWrapper.instance) {
      FetchWrapper.instance = new FetchWrapper(baseURL, options);
    }
    return FetchWrapper.instance;
  }

  /**
   * Sends a GET request.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async get<T>(url: string, config: IRequestOptions = {}): Promise<T> {
    const { queryParams, ..._config } = config;
    let _url = url;
    if (queryParams && !isEmptyObject(queryParams)) {
      const queryString = Object.entries(queryParams)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

      _url = queryString ? `${_url}?${queryString}` : _url;
    }

    const response = await this.request<T>(_url, { ..._config, method: "GET" });
    return response;
  }

  /**
   * Sends a POST request.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {any} data - The data to send in the request body.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async post<T>(url: string, data: any, config: IRequestOptions = {}): Promise<T> {
    const response = await this.request<T>(url, {
      ...config,
      method: "POST",
      body: JSON.stringify(data),
    });
    return response;
  }

  /**
   * Sends a PATCH request.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {any} data - The data to send in the request body.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async patch<T>(url: string, data: any, config: IRequestOptions = {}): Promise<T> {
    const response = await this.request<T>(url, {
      ...config,
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return response;
  }

  /**
   * Sends a PUT request.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {any} data - The data to send in the request body.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async put<T>(url: string, data: any, config: IRequestOptions = {}): Promise<T> {
    const response = await this.request<T>(url, {
      ...config,
      method: "PUT",
      body: JSON.stringify(data),
    });
    return response;
  }

  /**
   * Sends a DELETE request.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async delete<T>(url: string, config: IRequestOptions = {}): Promise<T> {
    const response = await this.request<T>(url, {
      ...config,
      method: "DELETE",
    });
    return response;
  }

  /**
   * Sends a generic HTTP request with the specified method.
   *
   * @template T - The expected response data type.
   * @param {string} url - The endpoint URL.
   * @param {IRequestOptions} [config={}] - Additional request options.
   * @returns {Promise<T>} A promise that resolves to the response data.
   */
  async request<T>(url: string, config: IRequestOptions = {}): Promise<T> {
    const fetchUrl = `${this.baseURL}${url}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json", // Default content type
      ...this.options.headers,
      ...config.headers,
    };

    if (i18n.language) {
      headers["Accept-Language"] = i18n.language;
    }
    const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
    if (this.tokensInstance.isTokenAboutToExpire(userAuth?.access) && config.withAuth) {
      await this.refreshToken();
    }

    if (config.withAuth) {
      headers.Authorization = `Bearer ${userAuth?.access}`;
    }

    const requestOptions: RequestInit = {
      method: "GET", // Default to GET method
      ...this.options,
      ...config,
      headers,
    };

    const response = await fetch(fetchUrl, requestOptions);

    if (response.status === 204) {
      // Handle a 204 No Content response here - we just return null
      return null as T;
    }

    const data = await response.json();

    if (!response.ok) {
      const error = {
        code: data.code,
        description: data.description,
        meta: { ...data.meta },
      };
      throw error;
    }

    return data;
  }
}

export default FetchWrapper;
